import React, { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Grid, File } from 'react-feather';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import config from '../../../../../config';

import ReportShell from '../../shared/ReportShell';
import ReportFilterBar from '../../shared/ReportFilterBar';
import ReportTable from '../../shared/ReportTable';
import ReportPagination from '../../shared/ReportPagination';
import ColumnPicker from '../../shared/ColumnPicker';
import useColumnConfig from '../../shared/useColumnConfig';
import useReportLookups from '../../shared/useReportLookups';
import useReportAccess from '../../shared/useReportAccess';

const PAGE_SIZE = 50;

const fmt2 = (v) => Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FIXED_COLUMNS = [
    { key: 'tenant', label: 'Tenant' },
    { key: 'tcode', label: 'Tcode' },
    { key: 'location', label: 'Location' },
    { key: 'month', label: 'Month' },
];
const DEFAULT_COLUMNS = FIXED_COLUMNS.map((c) => c.key);

// Day triplets (Net Sale / Txn / ATV per day) derived from row keys.
const buildDayColumns = (rows, t) => {
    if (!rows.length) return [];
    const days = Object.keys(rows[0])
        .filter((k) => k.startsWith('netsale_'))
        .map((k) => Number(k.split('_')[1]))
        .sort((a, b) => a - b);
    const net = days.map((d) => ({ key: `netsale_${d}`, label: t('Net Sale- Date {{day}}', { day: d }) }));
    const txn = days.map((d) => ({ key: `txn_${d}`, label: t('Txn- Date {{day}}', { day: d }) }));
    const atv = days.map((d) => ({ key: `atv_${d}`, label: t('ATV- Date {{day}}', { day: d }) }));
    return [...net, ...txn, ...atv];
};

const MONTH_OPTIONS = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' }, { value: '04', label: 'April' },
    { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' },
    { value: '09', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2]
    .map((y) => ({ value: String(y), label: String(y) }));
const CURRENT_MONTH = String(new Date().getMonth() + 1).padStart(2, '0');

const initialFilters = {
    year: String(CURRENT_YEAR),
    month: CURRENT_MONTH,
    property_id: '',
    category_id: '',
    subcategory_id: '',
    group_name: '',
    selectedTenants: [],
};

const ConsolidatedDaywiseReportView = () => {
    const { t } = useTranslation();
    const allowed = useReportAccess();

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [filters, setFilters] = useState(initialFilters);
    const {
        selectedColumns, onChange: onColumnsChange, columnOptions, visibleColumns: visibleFixed,
    } = useColumnConfig(FIXED_COLUMNS, DEFAULT_COLUMNS);
    const [reportData, setReportData] = useState([]);
    const [hasRun, setHasRun] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState({ key: '', dir: 'asc' });

    const lookups = useReportLookups({
        propertyId: filters.property_id,
        categoryId: filters.category_id,
        subcategoryId: filters.subcategory_id,
        groupName: filters.group_name,
    });
    const { concessionaireId, propertyAccessNames } = lookups;

    const onFilterChange = (key, value) => {
        setFilters((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'category_id') { next.subcategory_id = ''; next.selectedTenants = []; }
            if (key === 'property_id') { next.selectedTenants = []; }
            if (key === 'subcategory_id') { next.selectedTenants = []; }
            if (key === 'group_name') { next.selectedTenants = []; }
            return next;
        });
        setReportData([]);
        setHasRun(false);
        setPage(0);
    };

    const resetFilters = () => {
        setFilters(initialFilters);
        setReportData([]);
        setHasRun(false);
        setPage(0);
        setError('');
        setSort({ key: '', dir: 'asc' });
    };

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, {
            created_by: login_id,
            concessionaire_id: concessionaireId,
            module: 'Reports',
            task: `${login_name}${task}`,
        }, { headers }).catch((err) => console.warn('audit log failed', err?.response?.status));
    };

    const nameOf = (opts, val) => opts.find((o) => o.value === val)?.label || '';

    const generateReport = async () => {
        // Block future months.
        const now = new Date();
        const selected = new Date(Number(filters.year), Number(filters.month) - 1, 1);
        const current = new Date(now.getFullYear(), now.getMonth(), 1);
        if (selected > current) {
            setError(t('Future month reports are not allowed.'));
            setReportData([]);
            setHasRun(true);
            return;
        }
        setLoading(true);
        setError('');
        setPage(0);
        const payload = {
            concessionaire_id: concessionaireId,
            property_access: propertyAccessNames,
            year: Number(filters.year),
            month: Number(filters.month),
            group_name: filters.group_name,
            category_name: nameOf(lookups.categoryOptions, filters.category_id),
            property_name: nameOf(lookups.propertyOptions, filters.property_id),
            subcategory_name: nameOf(lookups.subcategoryOptions, filters.subcategory_id),
            tenant_id: filters.selectedTenants,
        };
        try {
            const res = await axios.post(`${config.hostname}/reports/getconsolidateddaywisereport`, payload, { headers });
            setReportData(Array.isArray(res.data) ? res.data : []);
            setHasRun(true);
            insertToLog(' generated Consolidated Daywise report');
        } catch (err) {
            console.error('consolidated daywise report failed', err);
            setError(err?.response?.data?.message || t('The report request failed. Please adjust your filters and try again.'));
            toast.error(t('Failed to generate Consolidated Daywise report'));
        } finally {
            setLoading(false);
        }
    };

    const dayColumns = useMemo(() => buildDayColumns(reportData, t), [reportData, t]);

    const sortedData = useMemo(() => {
        const data = [...reportData];
        if (!sort.key) return data;
        data.sort((a, b) => {
            const av = a[sort.key]; const bv = b[sort.key];
            const an = Number(av); const bn = Number(bv);
            if (!Number.isNaN(an) && !Number.isNaN(bn)) return sort.dir === 'asc' ? an - bn : bn - an;
            return sort.dir === 'asc'
                ? String(av ?? '').localeCompare(String(bv ?? ''))
                : String(bv ?? '').localeCompare(String(av ?? ''));
        });
        return data;
    }, [reportData, sort]);

    const pageCount = Math.ceil(sortedData.length / PAGE_SIZE);
    const pageRows = sortedData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const requestSort = (key) => {
        setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
    };

    const columns = useMemo(() => {
        const dayCols = dayColumns.map((c) => ({
            key: c.key,
            label: c.label,
            align: 'right',
            sortable: false,
            render: (item) => fmt2(item[c.key]),
        }));
        return [...visibleFixed.map((c) => ({ ...c, label: t(c.label) })), ...dayCols];
    }, [visibleFixed, dayColumns, t]);

    const fields = [
        { key: 'year', label: t('Year'), type: 'select', options: YEAR_OPTIONS, placeholder: t('Select year') },
        { key: 'month', label: t('Month'), type: 'select', options: MONTH_OPTIONS.map((o) => ({ ...o, label: t(o.label) })), placeholder: t('Select month') },
        { key: 'property_id', label: t('Property'), type: 'select', options: lookups.propertyOptions, placeholder: t('All properties') },
        { key: 'category_id', label: t('Category'), type: 'select', options: lookups.categoryOptions, placeholder: t('All categories') },
        { key: 'subcategory_id', label: t('Sub category'), type: 'select', options: lookups.subcategoryOptions, placeholder: t('All sub categories') },
        { key: 'group_name', label: t('Group'), type: 'select', options: lookups.groupOptions, placeholder: t('All groups') },
        { key: 'selectedTenants', label: t('Tenants'), type: 'multiselect', options: lookups.tenantOptions, placeholder: t('All tenants'), span: 2 },
    ];

    const allExportColumns = useMemo(() => [...FIXED_COLUMNS.map((c) => ({ ...c, label: t(c.label) })), ...dayColumns], [dayColumns, t]);

    const exportMeta = () => ([
        [t('Year'), filters.year],
        [t('Month'), t(nameOf(MONTH_OPTIONS, filters.month))],
        [t('Property'), nameOf(lookups.propertyOptions, filters.property_id) || t('All')],
        [t('Category'), nameOf(lookups.categoryOptions, filters.category_id) || t('All')],
        [t('Subcategory'), nameOf(lookups.subcategoryOptions, filters.subcategory_id) || t('All')],
        [t('Group'), filters.group_name || t('All')],
        [t('Exported By'), login_name],
        [t('Exported On'), new Date().toLocaleString()],
    ]);

    const exportToExcel = () => {
        if (!sortedData.length) return;
        const head = allExportColumns.map((c) => c.label);
        const rows = sortedData.map((row) => allExportColumns.map((c) => row[c.key] ?? ''));
        const ws = XLSX.utils.aoa_to_sheet([]);
        XLSX.utils.sheet_add_aoa(ws, [[t('Consolidated Daywise Sales Report')]], { origin: 'A1' });
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: head.length - 1 } }];
        XLSX.utils.sheet_add_aoa(ws, exportMeta(), { origin: 'A2' });
        const startRow = exportMeta().length + 3;
        XLSX.utils.sheet_add_aoa(ws, [head], { origin: `A${startRow - 1}` });
        XLSX.utils.sheet_add_aoa(ws, rows, { origin: `A${startRow}` });
        ws['!cols'] = allExportColumns.map((c) => ({ wch: c.key.startsWith('netsale_') || c.key.startsWith('txn_') || c.key.startsWith('atv_') ? 14 : 20 }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Report');
        XLSX.writeFile(wb, 'Consolidated_Daywise_Report.xlsx');
        insertToLog(' exported Consolidated Daywise report to excel');
    };

    const exportToPDF = () => {
        if (!sortedData.length) return;
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.setFontSize(14);
        doc.text(t('Consolidated Daywise Sales Report'), 14, 15);
        doc.setFontSize(9);
        let y = 24;
        exportMeta().forEach(([k, v]) => { doc.text(`${k}: ${v}`, 14, y); y += 6; });
        const head = allExportColumns.map((c) => c.label);
        const body = sortedData.map((row) => allExportColumns.map((c) => row[c.key] ?? ''));
        doc.autoTable({
            head: [head],
            body,
            startY: y + 4,
            styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
            theme: 'grid',
            headStyles: { fillColor: [0, 71, 178], textColor: 255, fontSize: 7, halign: 'center' },
            // 3 cols per day → far too wide for one page; paginate columns,
            // repeating the Tenant column on each horizontal page.
            horizontalPageBreak: true,
            horizontalPageBreakRepeat: 0,
            columnStyles: { 0: { cellWidth: 34 } },
        });
        doc.save('Consolidated_Daywise_Report.pdf');
        insertToLog(' exported Consolidated Daywise report to pdf');
    };

    if (!allowed) return null;

    return (
        <Fragment>
            <ReportShell
                title={t('Consolidated Daywise Report')}
                subtitle={t('Per-day net sales, transactions and ATV per tenant for the selected month.')}
                actions={(
                    <Fragment>
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={exportToExcel} disabled={!sortedData.length} title={t('Export to Excel')}>
                            <Grid size={14} /> {t('Excel')}
                        </button>
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={exportToPDF} disabled={!sortedData.length} title={t('Export to PDF')}>
                            <File size={14} /> {t('PDF')}
                        </button>
                    </Fragment>
                )}
            >
                <ReportFilterBar
                    fields={fields}
                    values={filters}
                    onChange={onFilterChange}
                    onGenerate={generateReport}
                    onReset={resetFilters}
                    loading={loading}
                />

                <ColumnPicker
                    options={columnOptions.map((o) => ({ ...o, label: t(o.label) }))}
                    value={selectedColumns}
                    total={FIXED_COLUMNS.length}
                    onChange={onColumnsChange}
                />

                <ReportTable
                    columns={columns}
                    rows={pageRows}
                    sort={sort}
                    onSort={requestSort}
                    loading={loading}
                    error={error}
                    emptyMessage={hasRun
                        ? t('No data found for the selected month and filters.')
                        : t('Choose your filters and select Generate report to view results.')}
                />

                <ReportPagination
                    page={page}
                    pageCount={pageCount}
                    onChange={setPage}
                    totalItems={sortedData.length}
                    pageSize={PAGE_SIZE}
                    itemLabel={t('tenants')}
                />
            </ReportShell>
        </Fragment>
    );
};

export default ConsolidatedDaywiseReportView;
